// ./src/utils/api/ConnectorService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// GraphQL queries
const CONNECTOR_LIST_QUERY = gql`
  query ConnectorList($filter: ConnectorFilter, $params: PaginationAndSorting) {
    connectorList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        name
        normalizedName
        description
        connectorCategory
        connectorType
        connectorConfiguration
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONNECTOR_GET_QUERY = gql`
  query ConnectorGet($id: ID) {
    connectorGet(id: $id) {
      code
      data {
        id
        accountId
        name
        normalizedName
        description
        connectorCategory
        connectorType
        connectorConfiguration
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONNECTOR_GET_MANY_QUERY = gql`
  query ConnectorGetMany($ids: [ID]) {
    connectorGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        name
        normalizedName
        description
        connectorCategory
        connectorType
        connectorConfiguration
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const CONNECTOR_CREATE_MUTATION = gql`
  mutation ConnectorCreate($params: ConnectorInput) {
    connectorCreate(params: $params) {
      code
      data {
        id
        accountId
        name
        normalizedName
        description
        connectorCategory
        connectorType
        connectorConfiguration
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONNECTOR_UPDATE_MUTATION = gql`
  mutation ConnectorUpdate(
    $where: ConnectorWhereInput
    $params: ConnectorInput
  ) {
    connectorUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        name
        normalizedName
        description
        connectorCategory
        connectorType
        connectorConfiguration
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONNECTOR_REMOVE_MUTATION = gql`
  mutation ConnectorRemove($where: ConnectorWhereInput) {
    connectorRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONNECTOR_REMOVE_MANY_MUTATION = gql`
  mutation ConnectorRemoveMany($where: [ConnectorWhereInput]) {
    connectorRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Connector {
  id?: string | undefined;
  accountId?: string | undefined;
  name?: string | undefined;
  normalizedName?: string | undefined;
  description?: string | undefined;
  connectorCategory?: string | undefined;
  connectorType?: string | undefined;
  connectorConfiguration?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  rights?: string[];
}

export interface ConnectorInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  name?: string | null | undefined;
  normalizedName?: string | null | undefined;
  description?: string | null | undefined;
  connectorCategory?: string | null | undefined;
  connectorType?: string | null | undefined;
  connectorConfiguration?: string | null | undefined;
  status?: number | null | undefined;
}

export interface ConnectorFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  normalizedName?: string[] | null | undefined;
  connectorCategory?: string[] | null | undefined;
  connectorType?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface ConnectorWhereInput {
  id?: string | undefined;
}

/**
 * Connector service that handles all connector-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class ConnectorService {
  /**
   * Get a list of connectors with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ConnectorFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CONNECTOR_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single connector by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(CONNECTOR_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple connectors by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(CONNECTOR_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new connector
   */
  static async create(params: ConnectorInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(CONNECTOR_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing connector
   */
  static async update(
    where: ConnectorWhereInput,
    params: ConnectorInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(CONNECTOR_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a connector
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(CONNECTOR_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple connectors
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      CONNECTOR_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
